import { RegisterClass } from "core/Misc/typeStore";
import { RewardGraphBuildState } from "../rewardGraphBuildState";
import { RewardGraphNode } from "../rewardGraphNode";
import { RewardGraphConnectionPoint } from "../rewardGraphConnectionPoint";
import { RewardGraphConnectionPointTypes } from "../rewardGraphConnectionPointTypes";
import { RewardStateOutputBlock } from "./RewardStateOutputBlock";
import { Nullable } from "core/types";

export class RewardStateInputBlock extends RewardGraphNode {
    private _source: Nullable<RewardStateOutputBlock> = null;
    /** @internal */
    public _tempStateSourceUniqueId: Nullable<number> = null;

    /**
     * @param name defines the block name
     */
    public constructor(name: string) {
        super(name);

        this.registerOutput("state", RewardGraphConnectionPointTypes.BasedOnInput);
    }

    public detach() {
        if (!this._source) {
            return;
        }
        this._source.detachFromSink(this);
    }

    /**
     * Gets the current class name
     * @returns the class name
     */
    public override getClassName() {
        return "RewardStateInputBlock";
    }

    public get source() {
        return this._source;
    }

    public set source( src : Nullable<RewardStateOutputBlock>) {
        this._source = src;
        this.name = "State Var: " + (src ? src._stateName : "Disconnected");
    }

    /**
     * Gets the reward input component
     */
    public get stateValue(): RewardGraphConnectionPoint {
        return this._outputs[0];
    }

    protected override _buildBlock(state: RewardGraphBuildState) {
        if (!this.source) {
            this.stateValue._storedFunction = null;
            this.stateValue._storedValue = null;
            return;
        }

        const stateName = this.source.stateName;
        this.stateValue._storedFunction = (state) => {
            return state.retrieveState( stateName );
        };
    }

    protected override _customBuildStep(state: RewardGraphBuildState): void {
        this.source?.build(state);
    }

    public override clone() {
        const clone = super.clone();

        if (this._source) {
            this._source.attachToSink(clone as RewardStateInputBlock);
        }

        return clone;
    }

    protected override _dumpPropertiesCode() {
        let codeString = super._dumpPropertiesCode();
        if (this._source) {
            codeString += `${this._source._codeVariableName}.attachToEndpoint(${this._codeVariableName});\n`;
        }
        return codeString;
    }

    override get isStateSink(): boolean {
        return true;
    }

    /**
     * Serializes this block in a JSON representation
     * @returns the serialized block object
     */
    public override serialize(): any {
        const serializationObject = super.serialize();
        serializationObject.source = this._source?.uniqueId ?? "";

        return serializationObject;
    }

    public override _deserialize(serializationObject: any) {
        super._deserialize(serializationObject);
        this._tempStateSourceUniqueId = serializationObject.entryPoint;
    }
}

RegisterClass("MELS.RewardStateInputBlock", RewardStateInputBlock);
